export interface SafeExceptionContext {
  requestId?: string;
  httpStatus?: number;
  method?: string;
  pathname?: string;
  trpcProcedure?: string;
  workspaceId?: string | number;
  userId?: string | number;
  app?: string;
  environment?: string;
  buildIdentity?: string;
  operation?: string;
  dependency?: string;
  componentStack?: string;
}

export interface ExceptionReportingAdapter {
  captureException(error: Error, context: SafeExceptionContext): Promise<void> | void;
}

export interface ExceptionReporter {
  report(error: unknown, context?: SafeExceptionContext): Promise<void>;
}

export type GlobalErrorHandler = (error: unknown, isFatal?: boolean) => void;

export interface GlobalHandlerHost {
  getGlobalHandler: () => GlobalErrorHandler | undefined;
  setGlobalHandler: (handler: GlobalErrorHandler | undefined) => void;
}

export const noOpAdapter: ExceptionReportingAdapter = {
  captureException: () => {},
};

export const noopAdapter = noOpAdapter;

function isSensitiveKey(key: string): boolean {
  const normalized = key.toLowerCase().replace(/[-_]/g, '');
  return (
    normalized.endsWith('apikey') ||
    normalized.endsWith('token') ||
    normalized.endsWith('secret') ||
    normalized.endsWith('password') ||
    normalized.endsWith('authorization') ||
    normalized.endsWith('cookie')
  );
}

export function sanitizeString(value: string): string {
  let result = value;

  // 1. Redact JWTs (3-part base64url strings, typically starting with eyJ)
  result = result.replace(
    /\beyJ[a-zA-Z0-9_-]+\.[a-zA-Z0-9_-]+\.[a-zA-Z0-9_\-./+=]+/g,
    '[REDACTED]'
  );
  result = result.replace(
    /\b[a-zA-Z0-9_-]{20,}\.[a-zA-Z0-9_-]{20,}\.[a-zA-Z0-9_\-./+=]{20,}\b/g,
    '[REDACTED]'
  );

  // 2. Redact Bearer tokens
  result = result.replace(/\bBearer\s+([a-zA-Z0-9_\-.~+/]+=*)/gi, 'Bearer [REDACTED]');

  // 3. Redact Authorization, Proxy-Authorization, Cookie, Set-Cookie headers
  result = result.replace(
    /\b(Authorization|Proxy-Authorization|Cookie|Set-Cookie):\s*(?:Bearer\s+)?([^\s\r\n,;'"}\]]+)/gi,
    '$1: [REDACTED]'
  );

  // 4. Redact full query string suffixes on URLs/paths (absolute, slash-prefixed, or relative) while preserving stack trace location info (:line:col)
  result = result.replace(
    /([^\s"'`<>()[\]{}?#]*)\?([^#\s\r\n"'`)\]}<>]+)/g,
    (_match, prefix, queryAndLoc) => {
      const locMatch = queryAndLoc.match(/^(.*)(:\d+:\d+)$/);
      if (locMatch) {
        return `${prefix}?[REDACTED]${locMatch[2]}`;
      }
      return `${prefix}?[REDACTED]`;
    }
  );

  // 5. Redact key=value pairs (assignment, form data)
  result = result.replace(
    /(["']?)([a-zA-Z0-9_-]+)\1\s*=\s*(["'])(.*?)\3/g,
    (match, quote1, key, quote2) => {
      if (isSensitiveKey(key)) {
        return `${quote1}${key}${quote1}=${quote2}[REDACTED]${quote2}`;
      }
      return match;
    }
  );
  result = result.replace(
    /(["']?)([a-zA-Z0-9_-]+)\1\s*=\s*([^&\s,;'"\]}]+)/g,
    (match, quote1, key) => {
      if (isSensitiveKey(key)) {
        return `${quote1}${key}${quote1}=[REDACTED]`;
      }
      return match;
    }
  );

  // 6. Redact JSON / object key-value pairs (key: value)
  result = result.replace(
    /(["']?)([a-zA-Z0-9_-]+)\1\s*:\s*(["'])(.*?)\3/g,
    (match, quote1, key, quote2) => {
      if (isSensitiveKey(key)) {
        return `${quote1}${key}${quote1}:${quote2}[REDACTED]${quote2}`;
      }
      return match;
    }
  );
  result = result.replace(
    /(["']?)([a-zA-Z0-9_-]+)\1\s*:\s*([^,\s{}[\]"']+)/g,
    (match, quote1, key) => {
      if (isSensitiveKey(key)) {
        return `${quote1}${key}${quote1}: [REDACTED]`;
      }
      return match;
    }
  );

  return result;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    const sanitizedMsg = sanitizeString(error.message);
    const normalized = new Error(sanitizedMsg);
    normalized.name = error.name;
    if (error.stack) {
      normalized.stack = sanitizeString(error.stack);
    }
    return normalized;
  }

  if (typeof error === 'string') {
    const sanitized = sanitizeString(error);
    const normalized = new Error(sanitized);
    normalized.name = 'Error';
    return normalized;
  }

  if (error && typeof error === 'object') {
    let rawMessage: string;
    if ('message' in error && typeof (error as { message?: unknown }).message === 'string') {
      rawMessage = (error as { message: string }).message;
    } else {
      try {
        rawMessage = JSON.stringify(error) || String(error);
      } catch {
        try {
          rawMessage = String(error);
        } catch {
          rawMessage = '[object Object]';
        }
      }
    }
    const sanitizedMsg = sanitizeString(rawMessage);
    const normalized = new Error(sanitizedMsg);

    try {
      if ('name' in error && typeof (error as { name?: unknown }).name === 'string') {
        normalized.name = (error as { name: string }).name;
      } else {
        normalized.name = 'Error';
      }
    } catch {
      normalized.name = 'Error';
    }

    try {
      if ('stack' in error && typeof (error as { stack?: unknown }).stack === 'string') {
        normalized.stack = sanitizeString((error as { stack: string }).stack);
      }
    } catch {
      // ignore
    }

    return normalized;
  }

  let msg: string;
  if (error === null) {
    msg = 'null';
  } else if (error === undefined) {
    msg = 'undefined';
  } else {
    try {
      msg = String(error);
    } catch {
      msg = 'unknown';
    }
  }
  const normalized = new Error(sanitizeString(msg));
  normalized.name = 'Error';
  return normalized;
}

function sanitizeContext(context?: SafeExceptionContext): SafeExceptionContext {
  if (!context || typeof context !== 'object') {
    return {};
  }

  const result: SafeExceptionContext = {};

  if (context.requestId !== undefined && context.requestId !== null) {
    result.requestId = sanitizeString(String(context.requestId));
  }
  if (
    context.httpStatus !== undefined &&
    context.httpStatus !== null &&
    typeof context.httpStatus === 'number'
  ) {
    result.httpStatus = context.httpStatus;
  }
  if (context.method !== undefined && context.method !== null) {
    result.method = sanitizeString(String(context.method));
  }
  if (context.pathname !== undefined && context.pathname !== null) {
    const raw = String(context.pathname);
    let cleanPath: string;
    try {
      if (raw.includes('://') || raw.startsWith('//')) {
        const url = new URL(raw, 'http://localhost');
        cleanPath = url.pathname;
      } else {
        const url = new URL(raw.startsWith('/') ? raw : `/${raw}`, 'http://localhost');
        cleanPath = raw.startsWith('/') ? url.pathname : url.pathname.replace(/^\//, '');
      }
    } catch {
      cleanPath = raw.split('?')[0].split('#')[0];
    }
    result.pathname = sanitizeString(cleanPath);
  }
  if (context.trpcProcedure !== undefined && context.trpcProcedure !== null) {
    result.trpcProcedure = sanitizeString(String(context.trpcProcedure));
  }
  if (context.workspaceId !== undefined && context.workspaceId !== null) {
    result.workspaceId =
      typeof context.workspaceId === 'number'
        ? context.workspaceId
        : sanitizeString(String(context.workspaceId));
  }
  if (context.userId !== undefined && context.userId !== null) {
    result.userId =
      typeof context.userId === 'number' ? context.userId : sanitizeString(String(context.userId));
  }
  if (context.app !== undefined && context.app !== null) {
    result.app = sanitizeString(String(context.app));
  }
  if (context.environment !== undefined && context.environment !== null) {
    result.environment = sanitizeString(String(context.environment));
  }
  if (context.buildIdentity !== undefined && context.buildIdentity !== null) {
    result.buildIdentity = sanitizeString(String(context.buildIdentity));
  }
  if (context.operation !== undefined && context.operation !== null) {
    result.operation = sanitizeString(String(context.operation));
  }
  if (context.dependency !== undefined && context.dependency !== null) {
    result.dependency = sanitizeString(String(context.dependency));
  }
  if (context.componentStack !== undefined && context.componentStack !== null) {
    result.componentStack = sanitizeString(String(context.componentStack));
  }

  return result;
}

export function createExceptionReporter(
  adapter: ExceptionReportingAdapter = noOpAdapter
): ExceptionReporter {
  const handler = async (error: unknown, context?: SafeExceptionContext): Promise<void> => {
    try {
      const normalizedError = normalizeError(error);
      const sanitizedCtx = sanitizeContext(context);
      await adapter.captureException(normalizedError, sanitizedCtx);
    } catch {
      // Rejection is contained
    }
  };

  return {
    report: handler,
  };
}

export function installGlobalExceptionReporter(
  reporter: ExceptionReporter,
  hostOrGetHandler: GlobalHandlerHost | (() => GlobalErrorHandler | undefined),
  setHandlerOrContext?:
    | ((handler: GlobalErrorHandler | undefined) => void)
    | SafeExceptionContext
    | (() => SafeExceptionContext),
  maybeContext?: SafeExceptionContext | (() => SafeExceptionContext)
): () => void {
  let getGlobalHandler: () => GlobalErrorHandler | undefined;
  let setGlobalHandler: (handler: GlobalErrorHandler | undefined) => void;
  let getContext: SafeExceptionContext | (() => SafeExceptionContext) | undefined;

  if (
    typeof hostOrGetHandler === 'object' &&
    hostOrGetHandler !== null &&
    'getGlobalHandler' in hostOrGetHandler
  ) {
    getGlobalHandler = hostOrGetHandler.getGlobalHandler.bind(hostOrGetHandler);
    setGlobalHandler = hostOrGetHandler.setGlobalHandler.bind(hostOrGetHandler);
    getContext = setHandlerOrContext as
      | SafeExceptionContext
      | (() => SafeExceptionContext)
      | undefined;
  } else {
    getGlobalHandler = hostOrGetHandler as () => GlobalErrorHandler | undefined;
    setGlobalHandler = setHandlerOrContext as (handler: GlobalErrorHandler | undefined) => void;
    getContext = maybeContext;
  }

  const oldHandler = getGlobalHandler();

  const newHandler: GlobalErrorHandler = (error: unknown, isFatal?: boolean) => {
    const context = typeof getContext === 'function' ? getContext() : getContext;
    try {
      const reportPromise = reporter.report(error, context);
      if (reportPromise && typeof (reportPromise as Promise<void>).catch === 'function') {
        (reportPromise as Promise<void>).catch(() => {
          // contained
        });
      }
    } catch {
      // contained
    }
    if (typeof oldHandler === 'function') {
      oldHandler(error, isFatal);
    }
  };

  setGlobalHandler(newHandler);

  return () => {
    setGlobalHandler(oldHandler);
  };
}
